import React from 'react';
import { useConversation } from '../../../contexts/ConversationContext';

// IntersectionObserver on chat bubbles — whatever bubble is centered
// in the viewport owns the current `viewportTurnId` (by turn-key). DAG
// renderers use this to dim non-viewport turns. Lives inside
// ConversationProvider so it can walk `state.messages` to map
// msgId → turnKey on each update.
export const ScrollObserver: React.FC<{
  onViewportTurn: (id: string | null) => void;
}> = ({ onViewportTurn }) => {
  const { state } = useConversation();
  const messages = state.messages;
  React.useEffect(() => {
    // Build a msgId → turnKey map once per messages change. Turn key
    // is the preceding user message id (and is itself for user msgs).
    const byId = new Map<string, string>();
    let lastUser: string | null = null;
    for (const m of messages) {
      if (m.role === 'user') {
        lastUser = m.id;
        byId.set(m.id, m.id);
      } else if (m.role === 'assistant' && lastUser) {
        byId.set(m.id, lastUser);
      }
    }

    let raf: number | null = null;
    let timer: number | null = null;
    let pending: string | null = null;

    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.find((e) => e.isIntersecting);
        if (!hit) return;
        const msgId = hit.target.getAttribute('data-message-id');
        if (!msgId) return;
        const turnKey = byId.get(msgId) ?? msgId;
        if (turnKey === pending) return;
        pending = turnKey;
        if (raf != null) cancelAnimationFrame(raf);
        if (timer != null) window.clearTimeout(timer);
        raf = requestAnimationFrame(() => {
          timer = window.setTimeout(() => {
            onViewportTurn(turnKey);
          }, 300); // bumped from 80ms so structural relayout under
          //          focal change doesn't flutter as the user scrolls.
        });
      },
      { rootMargin: '-30% 0% -30% 0%', threshold: 0 },
    );

    document.querySelectorAll('[data-message-id]').forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      if (raf != null) cancelAnimationFrame(raf);
      if (timer != null) window.clearTimeout(timer);
    };
  }, [messages, onViewportTurn]);
  return null;
};
